import { createContext, useContext, useCallback, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Clock, Trophy, Heart } from 'lucide-react';
import { cn } from '@/lib/utils';
import confetti from 'canvas-confetti';

interface MicroFeedbackContextType {
  showCelebration: (type: 'success' | 'milestone' | 'timeSaved', message: string) => void;
}

const MicroFeedbackContext = createContext<MicroFeedbackContextType | null>(null);

export const useMicroFeedback = () => {
  const context = useContext(MicroFeedbackContext);
  if (!context) {
    return {
      showCelebration: () => {},
    };
  }
  return context;
};

interface Notification {
  id: string;
  type: 'success' | 'milestone' | 'timeSaved';
  message: string;
}

export const MicroFeedbackProvider = ({ children }: { children: React.ReactNode }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    
    const handler = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  const showCelebration = useCallback((type: 'success' | 'milestone' | 'timeSaved', message: string) => {
    const id = Math.random().toString(36).slice(2);
    
    setNotifications(prev => [...prev, { id, type, message }]);

    if (!prefersReducedMotion && type === 'milestone') {
      confetti({
        particleCount: 60,
        spread: 50,
        origin: { y: 0.8 },
        colors: ['#10b981', '#3b82f6', '#8b5cf6'],
      });
    }

    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 4000);
  }, [prefersReducedMotion]);

  return (
    <MicroFeedbackContext.Provider value={{ showCelebration }}>
      {children}
      <MicroFeedback notifications={notifications} reducedMotion={prefersReducedMotion} />
    </MicroFeedbackContext.Provider>
  );
};

interface MicroFeedbackProps {
  notifications: Notification[];
  reducedMotion: boolean;
}

export const MicroFeedback = ({ notifications, reducedMotion }: MicroFeedbackProps) => {
  const getIcon = (type: Notification['type']) => {
    switch (type) {
      case 'success': return <Sparkles className="h-4 w-4" />;
      case 'milestone': return <Trophy className="h-4 w-4" />;
      case 'timeSaved': return <Clock className="h-4 w-4" />;
    }
  };

  const getColors = (type: Notification['type']) => {
    switch (type) {
      case 'success': return 'from-emerald-500/20 to-green-500/20 border-emerald-500/40 text-emerald-400';
      case 'milestone': return 'from-amber-500/20 to-orange-500/20 border-amber-500/40 text-amber-400';
      case 'timeSaved': return 'from-blue-500/20 to-indigo-500/20 border-blue-500/40 text-blue-400';
    }
  };

  return (
    <div className="fixed top-24 right-4 z-40 space-y-2 max-w-xs pointer-events-none">
      <AnimatePresence>
        {notifications.map((notification) => (
          <motion.div
            key={notification.id}
            initial={reducedMotion ? { opacity: 0 } : { opacity: 0, x: 50, scale: 0.8 }}
            animate={reducedMotion ? { opacity: 1 } : { opacity: 1, x: 0, scale: 1 }}
            exit={reducedMotion ? { opacity: 0 } : { opacity: 0, x: 50, scale: 0.8 }}
            className={cn(
              "flex items-center gap-3 px-4 py-3 rounded-xl border bg-gradient-to-r backdrop-blur-md shadow-lg",
              getColors(notification.type)
            )}
          >
            <div className="flex-shrink-0">
              {getIcon(notification.type)}
            </div>
            <p className="text-sm font-medium text-foreground">
              {notification.message}
            </p>
            {notification.type === 'success' && !reducedMotion && (
              <motion.div
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ repeat: 2, duration: 0.4 }}
              >
                <Heart className="h-4 w-4 text-pink-400" />
              </motion.div>
            )}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
};
